/**
 * Turn Denker's voice off or on by saying or typing it - "voice off", "stay
 * muted", "mute yourself" / "voice on", "speak again" - or `/voice` (`/voice
 * off`, `/voice on`; alone it switches): run here, never sent to the agent,
 * which cannot change it. A command leading a longer prompt ("stay muted,
 * open Google News") is run, and the rest goes on to the agent. "Stop
 * talking", "stop speaking" and "be quiet" only stop what is being said
 * (`voice-control-intents.ts`); a bare "mute" or "unmute" is the computer's,
 * for the agent. An answer to Denker's question is never a command.
 */
import { translate } from "@/i18n";
import { desktopVoiceMessages } from "@/i18n/messages/desktop-voice";
import { isTauri } from "@/lib/is-tauri";
import { useNotificationStore } from "@/stores/notification-store";
import { useTtsSettingsStore } from "@/stores/tts-settings-store";
import { useTTS } from "./use-tts";
import { announceVoiceChatCommand } from "./voice-command-feedback-store";

const OFF = [
  "voice off",
  "turn your voice off",
  "turn off your voice",
  "mute your voice",
  "mute yourself",
  "mute denker",
  "don't speak",
  "do not speak",
  "stay muted",
  "stay silent",
  "no voice",
];
const ON = [
  "voice on",
  "turn your voice on",
  "turn on your voice",
  "unmute your voice",
  "unmute yourself",
  "unmute denker",
  "speak again",
  "talk again",
  "talk to me again",
  "you can speak again",
  "start speaking",
];
/** Who is addressed before a command, and how long it lasts after it: words that change nothing. */
const ADDRESS =
  /^(?:(?:hey|ok|okay)[\s,]+)?(?:denker|danker|denka)[\s,.:-]+|^please[\s,]+/i;
const LASTING =
  /^(?:\s+(?:please|for now|for a while|from now on|again|in this (?:session|chat|conversation)|until i (?:tell|say|ask)[\w\s']*))*$/;

export type VoiceCommand = { on: boolean; rest: string };

const plain = (text: string) =>
  text.toLowerCase().replace(/[‘’]/g, "'").replace(/\s+/g, " ").trim();

/** The voice command `text` gives, and what of it is left for the agent. */
export function parseVoiceCommand(
  text: string,
  enabled: boolean,
): VoiceCommand | null {
  const slash = text.trim().match(/^\/voice(?:\s+(on|off))?\s*$/i);
  if (slash)
    return {
      on: slash[1] ? slash[1].toLowerCase() === "on" : !enabled,
      rest: "",
    };
  let asked = text.trim();
  while (ADDRESS.test(asked)) asked = asked.replace(ADDRESS, "");
  // The first clause, up to its first break; what follows it is the rest of the prompt.
  const [, clause = "", rest = ""] =
    asked.match(/^([^.!?;,\n]+)[.!?;,\n]*\s*([\s\S]*)$/) ?? [];
  const said = plain(clause);
  for (const [on, phrases] of [
    [false, OFF],
    [true, ON],
  ] as const) {
    const phrase = phrases.find(
      (one) => said === one || said.startsWith(`${one} `),
    );
    if (phrase && LASTING.test(said.slice(phrase.length)))
      return { on, rest: rest.trim() };
  }
  return null;
}

/** Run the voice command `text` gives, if any, in the desktop app; null when it gives none. */
export function runVoiceCommand(text: string): VoiceCommand | null {
  if (!isTauri()) return null;
  const settings = useTtsSettingsStore.getState();
  const command = parseVoiceCommand(text, settings.enabled);
  if (!command) return null;
  settings.setEnabled(command.on);
  if (!command.on) useTTS.getState().cancel("explicit");
  announceVoiceChatCommand(
    translate(
      desktopVoiceMessages,
      command.on ? "commandVoiceOn" : "commandVoiceOff",
    ),
  );
  useNotificationStore.getState().addSystemToast({
    id: `voice-command-${Date.now()}`,
    level: "info",
    title: translate(
      desktopVoiceMessages,
      command.on ? "voiceOnToast" : "voiceOffToast",
    ),
  });
  return command;
}
